import { useQuery } from '@tanstack/react-query';

import { UsageError } from '@/features/workspace-usage';

import { listTriggerProfiles } from '@/lib/api/generated/api';
import type { TriggerProfileView } from '@/lib/api/generated/model';

import { triggerKeys } from '../queryKeys';

/**
 * Every trigger profile configured on a workspace, enabled and disabled
 * alike (FR-005).
 *
 * Mirrors `useWorkspaceSecrets`: a hand-written `queryFn` calling the
 * generated raw fetcher directly, not the generated `getListTriggerProfilesQueryOptions`
 * — no feature in this codebase actually consumes orval's generated
 * `getXQueryOptions` helpers (`useWorkspace`'s `workspaceQueryOptions` is the
 * same hand-rolled shape), so this follows the pattern that's actually in
 * use rather than the one only sketched in planning.
 *
 * Anything but a 200 throws a typed `UsageError` rather than resolving to an empty list — the
 * same "failed load must never render as an empty state" reasoning
 * `useWorkspaceSecrets` documents, and just as true here: a workspace with
 * trigger profiles already configured must not look like one with none.
 */
export function useTriggerProfiles(workspaceId: string) {
  const query = useQuery({
    queryKey: triggerKeys.list(workspaceId),
    queryFn: async (): Promise<TriggerProfileView[]> => {
      const response = await listTriggerProfiles(workspaceId);
      // Typed, so a page switches on `kind` rather than on message text — the rule
      // `UsageError` records. Deliberately only `unavailable`: this endpoint's contract
      // is 200/401/422, so it has no 403 to map, and a `forbidden` branch here would be
      // unreachable code claiming a distinction the API cannot make. A refusal on this
      // surface is a 401, which the auth layer handles before a page ever renders.
      if (response.status !== 200) {
        throw new UsageError(
          'unavailable',
          'The trigger profiles could not be loaded.',
        );
      }
      return response.data;
    },
    retry: false,
  });

  return {
    profiles: query.data,
    isPending: query.isPending,
    loadFailed: query.isError,
    refetch: query.refetch,
  };
}
